import React, {useEffect, useMemo, useState} from "react";
import {Box} from "@mui/material";
import {useGetRegByOperationQuery} from "../analytics_api";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {GridState} from "../../../libs/grid_state_reducers";
import {
    setColumns,
    setDensity,
    setEndTime,
    setFilter,
    setMachine,
    setOperation,
    setPaginationModel,
    setPartner,
    setProduct,
    setSort,
    setStartTime,
    setUser,
    setWorker,
    setWorkOrder
} from "./regbyoperation_grid_slice";
import {DataGridPro, GridColDef} from "@mui/x-data-grid-pro";
import DateTimeFilter from "../../../components/DateTimeFilter";
import OperationFilter from "../../operation/OperationFilter";
import MachineFilter from "../../machines/MachineFilter";
import PersonFilter from "../../people/PersonFilter";
import UserFilter from "../../users/UserFilter";
import ProductFilter from "../../products/ProductFilter";
import WorkOrderFilter from "../../workOrders/WorkOrderFilter";
import PartnerFilter from "../../partners/PartnerFilter";
import {FilterItem, RegFilters} from "../registrations/reg_grid_slice";
import RegGrid from "../ItemRegGrid";
import AnalyticsGridToolbar from "../AnalyticsGridToolbar";
import AnalyticsGridFooter from "../AnalyticsGridFooter";
import FilterSwitcher from "../FilterSwitcher";
import {AnaliticsFilterToolbar} from "../../../components/styled";

export default () => {
    const dispatch = useDispatch();
    const gridState = useSelector<RootState, GridState & RegFilters>((state) => state.regByOperationGrid)

    const {data, error, isFetching} = useGetRegByOperationQuery(gridState)

    const columns = useMemo<GridColDef<any>[]>(() => {
        return [
            {
                field: 'Code',
                headerName: 'Koda',
                valueGetter: (value, row) => row.workTask.Code,
                width: 100
            },
            {
                field: 'Name',
                headerName: 'Operacija',
                valueGetter: (value, row) => row.workTask.Name,
                width: 150
            },
            {
                field: "count",
                headerName: 'Št. registracij',
            },
            {
                field: "sum",
                headerName: 'Trajanje',
                valueFormatter: (val) => {
                    const hours = Math.trunc(val / 3600);
                    const minutes = Math.round((val - hours * 3600) / 60);
                    return hours + ":" + (`0${minutes}`).slice(-2)
                }
            },
            {
                field: "sumMinutes",
                headerName: 'Trajanje (minute)',
                valueGetter: (value, row) => row.sum,
                valueFormatter: (val) => {
                    return Math.round(val / 60);
                }
            },
        ]
    }, [])

    useEffect(() => {
        dispatch(setColumns({
            Name: true,
        }))
    }, [])

    const [hours, setHours] = useState(0)
    useEffect(() => {
        if (data) {
            let sum = 0
            data.result.forEach(row => {
                sum += row.sum
            })
            setHours(sum)
            return
        }
        setHours(0)
    }, [data])

    const availableFilters = useMemo(() => new Set<FilterItem>(["machine", "operation", "worker", "workOrder", "user", "partner", "product"]), [])
    const [seq, setSeq] = useState(0)
    const enabledFilters: Set<FilterItem> = new Set<FilterItem>(JSON.parse(localStorage.getItem("filters_operations") || "[]"));
    const activeFilters = new Set<FilterItem>()
    if (gridState.operation.length) activeFilters.add("operation");
    if (gridState.machine.length) activeFilters.add("machine");
    if (gridState.workOrder.length) activeFilters.add("workOrder");
    if (gridState.worker.length) activeFilters.add("worker");
    if (gridState.user.length) activeFilters.add("user");
    if (gridState.partner.length) activeFilters.add("partner");
    if (gridState.product.length) activeFilters.add("product");
    const showFilter = (filter: FilterItem) => activeFilters.has(filter) || enabledFilters.has(filter)


    return <Box
        sx={{pb: "1em", pt: "1em"}}>
        <Box sx={{
            display: "flex",
            maxWidth: "100%"
        }}>
            <AnaliticsFilterToolbar>
                <DateTimeFilter label="Začetek" value={gridState.startTime}
                                onChange={(val) => dispatch(setStartTime(val))}/>
                <DateTimeFilter label="Konec" value={gridState.endTime} onChange={(val) => dispatch(setEndTime(val))}/>
                {showFilter("operation") &&
                    <OperationFilter value={gridState.operation} onChange={(val) => dispatch(setOperation(val))}/>}
                {showFilter("machine") &&
                    <MachineFilter
                        value={gridState.machine}
                        onChange={(val) => dispatch(setMachine(val))}/>}
                {showFilter("worker") &&
                    <PersonFilter
                        value={gridState.worker}
                        onChange={(val) => dispatch(setWorker(val))}/>}
                {showFilter("user") && <UserFilter
                    value={gridState.user}
                    onChange={(val) => dispatch(setUser(val))}/>}
                {showFilter("product") &&
                    <ProductFilter
                        value={gridState.product}
                        onChange={(val) => dispatch(setProduct(val))}/>}
                {showFilter("workOrder") &&
                    <WorkOrderFilter
                        value={gridState.workOrder}
                        onChange={(val) => dispatch(setWorkOrder(val))}/>}
                {showFilter("partner") &&
                    <PartnerFilter
                        value={gridState.partner}
                        onChange={(val) => dispatch(setPartner(val))}/>}
            </AnaliticsFilterToolbar>
            <Box>
                <FilterSwitcher
                    availableFilters={availableFilters}
                    onChange={(vals) => {
                        console.log(vals)
                        localStorage.setItem("filters_operations", JSON.stringify(Array.from(vals)))
                        setSeq(seq + 1)
                    }}
                    enabled={enabledFilters}
                    active={activeFilters}
                />
            </Box>
        </Box>
        <Box>
            <DataGridPro
                slots={{
                    toolbar: AnalyticsGridToolbar,
                    footer: () => AnalyticsGridFooter(hours)
                }}
                columns={columns}
                paginationModel={gridState.paginationModel}
                onPaginationModelChange={model => dispatch(setPaginationModel(model))}
                pageSizeOptions={[50, 100, 500, 5000]}
                density={gridState.density}
                onDensityChange={(density) => dispatch(setDensity(density))}
                pagination
                filterMode="server"
                sortModel={gridState.sort}
                onSortModelChange={(model) => dispatch(setSort(model))}
                onFilterModelChange={(model) => {
                    dispatch(setFilter(model))
                }}
                columnVisibilityModel={gridState.columns}
                loading={isFetching}
                onColumnVisibilityModelChange={(e) => dispatch(setColumns(e))}
                rows={data?.result || []}
                getRowId={(row) => row.workTask.id}
                getDetailPanelContent={(row) => <RegGrid gridState={gridState} groupByFilter={{operation: [row.row.workTask]}}/>}
                getDetailPanelHeight={({row}) => 'auto'}
            />
        </Box>
    </Box>;
}